import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState, type ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '@/app/auth/context';
import { PageHeader } from '@/components/admin/AdminShell';
import { ListTable, type Column, type ListState } from '@/components/admin/ListTable';
import { Notice } from '@/components/admin/Notice';
import { fetchInvitations } from '@/features/invitations/api';
import { PendingInvitations } from '@/features/invitations/PendingInvitations';
import {
  changeOfficeGrade,
  changeOfficeStatus,
  fetchOfficeAccounts,
  type OfficeAccount,
} from '@/features/officeAccounts/api';
import { ApiError } from '@/lib/api/http';

const GRADE_LABELS = { office_admin: '管理責任者', office_member: '一般' } as const;

/**
 * 事務局アカウント管理（指示書 §5.1 / §10）。
 * 管理責任者を0人にする操作・自己降格・自己停止は API 側で拒否される。
 * ここで出るエラーメッセージはそのまま表示する（画面ではなく API が判断している）。
 */
export function OfficeAccountsPage(): ReactElement {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.office_grade === 'office_admin' || user?.active_role === 'super_admin';
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: ['office-accounts'], queryFn: fetchOfficeAccounts });
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState('');

  // 招待中（まだ受諾されていない）の事務局アカウント（D-140）
  const invitationsQuery = useQuery({
    queryKey: ['invitations', 'office-pending'],
    queryFn: () => fetchInvitations(1, 100),
    enabled: isAdmin,
  });
  const pendingInvitations = (invitationsQuery.data?.items ?? []).filter(
    (i) => i.status === 'pending' && i.role === 'office',
  );

  const filteredRows = useMemo(() => {
    const rows = query.data ?? [];
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (a) =>
        (a.display_name ?? '').toLowerCase().includes(q) || a.email.toLowerCase().includes(q),
    );
  }, [query.data, search]);

  let state: ListState = 'ready';
  if (query.isPending) state = 'loading';
  else if (query.isError) {
    state = query.error instanceof ApiError && query.error.status === 403 ? 'forbidden' : 'error';
  }

  async function onToggleGrade(account: OfficeAccount): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const next = account.office_grade === 'office_admin' ? 'office_member' : 'office_admin';
      await changeOfficeGrade(account.user_id, next);
      setNotice(`${account.email} を${GRADE_LABELS[next]}に変更しました。`);
      await queryClient.invalidateQueries({ queryKey: ['office-accounts'] });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '変更に失敗しました。');
    } finally {
      setBusy(false);
    }
  }

  async function onToggleStatus(account: OfficeAccount): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await changeOfficeStatus(account.user_id, !account.is_active);
      setNotice(`${account.email} を${account.is_active ? '停止' : '有効化'}しました。`);
      await queryClient.invalidateQueries({ queryKey: ['office-accounts'] });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '変更に失敗しました。');
    } finally {
      setBusy(false);
    }
  }

  const columns: Column<OfficeAccount>[] = [
    { key: 'email', header: 'メール', primary: true, render: (a) => a.email },
    { key: 'display_name', header: '表示名', render: (a) => a.display_name ?? '—' },
    { key: 'grade', header: '権限区分', render: (a) => GRADE_LABELS[a.office_grade] },
    { key: 'status', header: '状態', render: (a) => (a.is_active ? '有効' : '停止中') },
    { key: 'totp', header: '2要素認証', render: (a) => (a.totp_enrolled ? '設定済み' : '未設定') },
  ];

  return (
    <>
      <PageHeader
        title="事務局アカウント管理"
        description="管理責任者を0人にする操作・自分自身の降格/停止はできません。"
        actions={
          isAdmin ? (
            <button
              type="button"
              onClick={() => navigate('/office-accounts/new')}
              className="rounded-sm border border-transparent bg-wpaccent px-3.5 py-2 text-white hover:bg-wpaccent-hover"
            >
              招待する
            </button>
          ) : undefined
        }
      />

      {notice && (
        <Notice kind="success" onDismiss={() => setNotice(null)}>
          {notice}
        </Notice>
      )}
      <PendingInvitations
        invitations={pendingInvitations}
        invalidateKeys={[['invitations', 'office-pending']]}
        describe={(i) => (i.office_grade === 'office_admin' ? '管理責任者' : '一般')}
      />

      {error && (
        <Notice kind="error" dismissible={false}>
          {error}
        </Notice>
      )}

      <ListTable<OfficeAccount>
        caption="事務局アカウント一覧"
        rows={filteredRows}
        columns={columns}
        rowKey={(a) => a.user_id}
        rowLabel={(a) => a.display_name ?? a.email}
        state={state}
        errorMessage={query.error instanceof Error ? query.error.message : undefined}
        onRetry={() => void query.refetch()}
        searchValue={search}
        onSearch={(value) => setSearch(value)}
        searchPlaceholder="氏名・メールで検索"
        rowActions={[
          {
            label: '権限区分を切替',
            onSelect: (a) => !busy && void onToggleGrade(a),
          },
          {
            label: '有効・停止を切替',
            onSelect: (a) => !busy && void onToggleStatus(a),
            destructive: true,
          },
        ]}
        emptyTitle="事務局アカウントがありません"
      />
    </>
  );
}
